import { useEffect } from 'react';

import { css, cx } from '@emotion/css';
import { Col, Row } from 'antd';
import { isNumber } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import { doGetQuote } from 'src/actions';
import { CurrencyField, MSpinner, MTypography, PercentageField } from 'src/lib';
import { Quote } from 'src/models';
import { BaseStyle, ColorConstant, FlexDirection } from 'src/styles';
import { Maybe } from 'src/utils';

const securityDetailsStyle = css`
  height: 300px;
`;

export const QuoteTextField = ({ label, value }: { label: string; value: string | number | JSX.Element }) => {
  return (
    <Row className={FlexDirection.column}>
      <MTypography variant='h3' color={ColorConstant.BRAND6} align='center'>
        {value}
      </MTypography>
      <MTypography variant='subtitle1' align='center'>
        {label}
      </MTypography>
    </Row>
  );
};

interface SecurityDetailsStatsProps {
  symbol?: string;
}

export const SecurityDetailsStats = ({ symbol }: SecurityDetailsStatsProps) => {
  const dispatch = useDispatch();
  const isLoading = useSelector((state: any) => Boolean(state.quotes.details.isLoading));
  const quoteDetailsData: Maybe<Quote> = useSelector((state: any) => state.quotes.details.data);

  useEffect(() => {
    if (quoteDetailsData?.symbol === symbol) return;

    if (symbol) {
      dispatch(doGetQuote({ params: { symbol } }));
    }
  }, [dispatch, symbol, quoteDetailsData?.symbol]);

  if (isLoading) {
    return <MSpinner height='300px' />;
  }

  return (
    <Row className={cx(BaseStyle.level, securityDetailsStyle)}>
      <Col span={8}>
        <QuoteTextField
          label='Day High'
          value={isNumber(quoteDetailsData?.highPrice) ? <CurrencyField value={quoteDetailsData?.highPrice} /> : '-'}
        />
      </Col>
      <Col span={8}>
        <QuoteTextField
          label='Change'
          value={
            isNumber(quoteDetailsData?.change) ? (
              <PercentageField
                value={quoteDetailsData?.change}
                color={(quoteDetailsData?.change as number) >= 0 ? ColorConstant.GREEN6 : ColorConstant.RED6}
              />
            ) : (
              '-'
            )
          }
        />
      </Col>
      <Col span={8}>
        <QuoteTextField
          label='Day Low'
          value={isNumber(quoteDetailsData?.lowPrice) ? <CurrencyField value={quoteDetailsData?.lowPrice} /> : '-'}
        />
      </Col>
      <Col span={8}>
        <QuoteTextField
          label='Bid'
          value={isNumber(quoteDetailsData?.bidPrice) ? <CurrencyField value={quoteDetailsData?.bidPrice} /> : '-'}
        />
      </Col>
      <Col span={8}>
        <QuoteTextField
          label='Open Price'
          value={isNumber(quoteDetailsData?.openPrice) ? <CurrencyField value={quoteDetailsData?.openPrice} /> : '-'}
        />
      </Col>
      <Col span={8}>
        <QuoteTextField
          label='Ask'
          value={isNumber(quoteDetailsData?.askPrice) ? <CurrencyField value={quoteDetailsData?.askPrice} /> : '-'}
        />
      </Col>
      <Col span={8}>
        <QuoteTextField
          label='Prev. Close'
          value={isNumber(quoteDetailsData?.closePrice) ? <CurrencyField value={quoteDetailsData?.closePrice} /> : '-'}
        />
      </Col>
      <Col span={8}>
        <QuoteTextField
          label='Volume'
          value={isNumber(quoteDetailsData?.volume) ? (quoteDetailsData?.volume as number) : '-'}
        />
      </Col>
      <Col span={8}>
        <QuoteTextField label='Exchange' value={quoteDetailsData?.exchange ? quoteDetailsData?.exchange : '-'} />
      </Col>
    </Row>
  );
};
